import React, { useState } from "react";
import Translation from "libs/Translation";
import Text from "components/common/Text";
import Image from "next/image";
import { Tabs, Tab, Card, CardContent, Box } from "@mui/material";
import {
  Droplets,
  Wind,
  Brain,
  Bone,
  Eye,
  Stethoscope,
  HeartPulse,
  Activity,
  Syringe,
  Ambulance,
  Pill,
} from "lucide-react";

const SERVICE_TABS = [
  {
    value: "superspecialty",
    label: "کلینیک فوق تخصصی",
    items: [
      { title: "کلیه و مجاری ادرار", icon: Droplets },
      { title: "آسم و آلرژی", icon: Wind },
      { title: "اعصاب و روان", icon: Brain },
      { title: "ارتوپدی", icon: Bone },
      { title: "جراحی مغز و اعصاب", icon: Brain },
      { title: "چشم", icon: Eye },
      { title: "گوارش", icon: Stethoscope },
      { title: "قلب", icon: HeartPulse },
      { title: "غدد", icon: Activity },
    ],
  },
  {
    value: "specialty",
    label: "کلینیک‌های تخصصی",
    items: [
      { title: "قلب و عروق", icon: HeartPulse },
      { title: "بینایی سنجی", icon: Eye },
      { title: "چشم", icon: Eye },
      { title: "اعصاب و روان", icon: Brain },
      { title: "دیابت و غدد", icon: Activity },
      { title: "تغذیه", icon: Activity },
      { title: "زنان و زایمان", icon: Stethoscope },
      { title: "اطفال", icon: HeartPulse },
      { title: "گوش ، حلق و بینی", icon: Activity },
      { title: "داخلی", icon: Stethoscope },
      { title: "پوست و مو", icon: Droplets },
      { title: "ارتوپدی", icon: Bone },
    ],
  },
  {
    value: "paraclinic",
    label: "پاراکلینیک",
    items: [
      { title: "آزمایشگاه", icon: Activity },
      { title: "رادیولوژی", icon: Activity },
      { title: "فیزیوتراپی", icon: Activity },
      { title: "دندانپزشکی", icon: Activity },
      { title: "تزریقات", icon: Syringe },
    ],
  },
  {
    value: "dailyUnits",
    label: "واحدهای شبانه‌روزی",
    items: [
      { title: "اورژانس", icon: Ambulance },
      { title: "داروخانه", icon: Stethoscope },
      { title: "آمبولانس", icon: Ambulance },
    ],
  },
];

const Services = ({ center, mobileView, onServiceClick }) => {
  const hasServices = center?.Services?.length;
  const [currentTab, setCurrentTab] = useState(SERVICE_TABS[0].value);

  const activeTab = SERVICE_TABS.find((t) => t.value === currentTab);

  return (
    <div className="box padding-24">
      {hasServices && (
        <>
          <div className="row-flex flex-title">
            <Image
              src="/nextasset/images/center/icon-1.png"
              width={mobileView ? 28 : 32}
              height={mobileView ? 28 : 32}
              alt="text-icon"
            />
            <Text color="#0D2439" className="title">
              {Translation.t("title.speciality-groups")}
            </Text>
          </div>
          <Box sx={{ mt: 2, direction: "rtl" }}>
            <Tabs
              value={currentTab}
              onChange={(e, v) => setCurrentTab(v)}
              variant="scrollable"
              scrollButtons="auto"
            >
              {SERVICE_TABS.map((tab) => (
                <Tab
                  key={tab.value}
                  value={tab.value}
                  label={tab.label}
                  sx={{ fontSize: { xs: 12, sm: 14 }, minHeight: 40 }}
                />
              ))}
            </Tabs>
            {activeTab && (
              <Box
                sx={{
                  mt: 3,
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                  gap: 2,
                }}
              >
                {activeTab.items.map((item) => {
                  const Icon = item.icon || Stethoscope;
                  return (
                    <Card
                      key={item.title}
                      elevation={2}
                      onClick={() => onServiceClick?.(item.title)}
                      sx={{
                        borderRadius: 3,
                        cursor: "pointer",
                        transition: "all 0.2s",
                        border: "1px solid transparent",
                        "&:hover": {
                          borderColor: "#29bcab",
                          transform: "translateY(-4px)",
                          backgroundColor: "#f0fdfa",
                        },
                      }}
                    >
                      <CardContent
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          py: 2.5,
                        }}
                      >
                        <Box
                          sx={{
                            mb: 1.5,
                            width: 48,
                            height: 48,
                            borderRadius: 3,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "#29bcab",
                          }}
                        >
                          <Icon size={24} color="#ffffff" />
                        </Box>
                        <span
                          style={{
                            fontSize: 14,
                            color: "#0D2439",
                            fontWeight: 500,
                            textAlign: "center",
                          }}
                        >
                          {item.title}
                        </span>
                      </CardContent>
                    </Card>
                  );
                })}
              </Box>
            )}
          </Box>
        </>
      )}
      {center?.Facilities?.length && (
        <>
          {hasServices && <div className="line" />}
          <div className="row-flex flex-title">
            <Image
              src="/nextasset/images/center/icon-2.png"
              width={mobileView ? 28 : 32}
              height={mobileView ? 28 : 32}
              alt="text-icon"
            />
            <Text color="#0D2439" className="title">
              {Translation.t("title.facilities")}
            </Text>
          </div>
          <div className="facilities-list">
            {center.Facilities.map((f, index) => (
              <span className="item gap-8" key={index}>
                <Image
                  src="/nextasset/images/center/tick-circle.png"
                  width={18}
                  height={18}
                  alt="text-icon"
                />
                {Translation.n(f.label)}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export default Services;
